import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase/config';
import { v4 as uuidv4 } from 'uuid';

export interface ChatFileUpload {
  url: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadPath: string;
}

export class ChatFileUploadService {
  private static instance: ChatFileUploadService;

  static getInstance(): ChatFileUploadService {
    if (!ChatFileUploadService.instance) {
      ChatFileUploadService.instance = new ChatFileUploadService();
    }
    return ChatFileUploadService.instance;
  }

  /**
   * Upload a PDF blob to Firebase Storage for FIN Chat
   */
  async uploadPDFToChat(pdfBlob: Blob, fileName: string, chatId: string): Promise<ChatFileUpload> {
    try {
      console.log('📤 Uploading PDF to Firebase Storage...', { fileName, chatId, size: pdfBlob.size });
      
      // Generate unique filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const uniqueId = uuidv4().substring(0, 8);
      const safeFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storageFileName = `${timestamp}_${uniqueId}_${safeFileName}`;
      
      // Create storage path: chat-files/{chatId}/{filename}
      const storagePath = `chat-files/${chatId}/${storageFileName}`;
      const storageRef = ref(storage, storagePath);
      
      // Set metadata
      const metadata = {
        contentType: 'application/pdf',
        customMetadata: {
          originalFileName: fileName,
          uploadedBy: 'system',
          uploadType: 'store-goal-report',
          chatId: chatId
        }
      };
      
      // Upload the blob
      const uploadResult = await uploadBytes(storageRef, pdfBlob, metadata);
      console.log('✅ PDF uploaded successfully:', uploadResult.metadata.fullPath);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      console.log('✅ PDF download URL generated:', downloadURL);
      
      return {
        url: downloadURL,
        fileName: fileName,
        fileSize: pdfBlob.size,
        fileType: 'application/pdf',
        uploadPath: storagePath
      };
      
    } catch (error) {
      console.error('❌ Failed to upload PDF to Firebase Storage:', error);
      throw new Error(`PDF upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Upload any file blob to Firebase Storage for FIN Chat
   */
  async uploadFileToChat(fileBlob: Blob, fileName: string, fileType: string, chatId: string): Promise<ChatFileUpload> {
    try {
      console.log('📤 Uploading file to Firebase Storage...', { fileName, fileType, chatId, size: fileBlob.size });
      
      // Validate file size (max 10MB for chat files)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (fileBlob.size > maxSize) {
        throw new Error(`File size (${(fileBlob.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (10MB)`);
      }
      
      // Generate unique filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const uniqueId = uuidv4().substring(0, 8);
      const safeFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storageFileName = `${timestamp}_${uniqueId}_${safeFileName}`;
      
      // Create storage path
      const storagePath = `chat-files/${chatId}/${storageFileName}`;
      const storageRef = ref(storage, storagePath);
      
      // Set metadata
      const metadata = {
        contentType: fileType,
        customMetadata: {
          originalFileName: fileName,
          uploadedBy: 'system',
          uploadType: 'chat-attachment',
          chatId: chatId
        }
      };
      
      // Upload the blob
      const uploadResult = await uploadBytes(storageRef, fileBlob, metadata);
      console.log('✅ File uploaded successfully:', uploadResult.metadata.fullPath);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      console.log('✅ File download URL generated:', downloadURL);
      
      return {
        url: downloadURL,
        fileName: fileName,
        fileSize: fileBlob.size,
        fileType: fileType,
        uploadPath: storagePath
      };
      
    } catch (error) {
      console.error('❌ Failed to upload file to Firebase Storage:', error);
      throw new Error(`File upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on file type
   */
  getFileIcon(fileType: string): string {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('video')) return '🎥';
    if (fileType.includes('audio')) return '🎵';
    if (fileType.includes('text')) return '📝';
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) return '📊';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📽️';
    if (fileType.includes('word') || fileType.includes('document')) return '📄';
    return '📎';
  }
}

export const chatFileUploadService = ChatFileUploadService.getInstance();
